import type { ReactNode } from 'react';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Profile } from '@/utils/session/get-profile';

import type { ResolvedConsent } from './consent-cookie';
import type { ConsentChoices, ConsentDecisionSource } from './consent-signals';

import { ConsentContext } from './consent-context';
import { resolveStoredConsent, writeConsentCookie } from './consent-cookie';
import { decideConsentDefaults, hasImpliedConsent } from './consent-defaults';
import {
  choicesFromProfileCookieSettings,
  profileConsentVersion,
} from './consent-profile';
import {
  decideConsentPrompt,
  isEmbeddedInIframe,
} from './consent-prompt-policy';
import {
  CURRENT_CONSENT_VERSION,
  UNSET_ALL_CHOICES,
  mergeChoices,
  signalsFromChoices,
} from './consent-signals';

type ConsentProviderProps = {
  children: ReactNode;
  profile: Profile | null;
  isSignedIn: boolean;
  regionCode: string | undefined;
  isPublicApplyRoute: boolean;
  isLocalEnvironment: boolean;
};

/**
 * Owns consent resolution for the whole app.
 *
 * Cookies first, then the profile copy, and `decideConsentDefaults` for
 * whatever none of them answered. Only the banner and the settings panel
 * record a decision: resolution and the implied defaults are reads, and the
 * defaults last no longer than this pageview.
 *
 * What it publishes is split accordingly — `signals` carries those defaults,
 * `answers` never does. See `ConsentContextValue`.
 */
export function ConsentProvider({
  children,
  profile,
  isSignedIn,
  regionCode,
  isPublicApplyRoute,
  isLocalEnvironment,
}: ConsentProviderProps) {
  const [isResolved, setIsResolved] = useState(false);
  const [stored, setStored] = useState<ResolvedConsent | null>(null);
  const [isEmbedded, setIsEmbedded] = useState(false);

  // Read through a ref so the resolution effect depends only on the stored
  // settings' *content*, not on the object identity the query cache hands us
  // on every render.
  const profileRef = useRef(profile);
  profileRef.current = profile;
  const profileCookieSettingsKey = JSON.stringify(
    profile?.cookieSettings ?? null,
  );

  useEffect(() => {
    // Cookies and `window.top` are browser-only: resolving in an effect keeps
    // the server render and the first client render identical.
    setIsEmbedded(isEmbeddedInIframe());

    const cookieSettings = profileRef.current?.cookieSettings;
    setStored(
      resolveStoredConsent({
        profileChoices: choicesFromProfileCookieSettings(cookieSettings),
        profileConsentVersion: profileConsentVersion(cookieSettings),
      }),
    );
    setIsResolved(true);
  }, [profileCookieSettingsKey]);

  const save = useCallback(
    (choices: ConsentChoices, src: ConsentDecisionSource) => {
      setStored(writeConsentCookie(choices, src, CURRENT_CONSENT_VERSION));
    },
    [],
  );

  const value = useMemo(() => {
    const answers = stored?.choices ?? UNSET_ALL_CHOICES;
    // The region's implied grants are the base layer and a stored answer wins
    // over them, so an unanswered category — and only an unanswered one —
    // falls back to the policy.
    //
    // The overlay leaves this provider as `signals` and in no other form.
    // Everything else — the banner, the settings panel, the profile sync,
    // signup, the prompt decision — reads `answers`, so a default inferred for
    // one pageview can neither be stamped as a decision nor silence a question
    // the visitor was never asked.
    const effective = mergeChoices(
      decideConsentDefaults({ isSignedIn, regionCode }),
      answers,
    );
    return {
      isResolved,
      stored,
      signals: signalsFromChoices(effective),
      answers,
      promptMode: isResolved
        ? decideConsentPrompt({
            hasStoredRecord: stored !== null,
            hasImpliedConsent: hasImpliedConsent({ isSignedIn, regionCode }),
            choices: answers,
            isReconciled: stored?.isReconciled ?? false,
            storedConsentVersion: stored?.v ?? null,
            isEmbedded,
            isPublicApplyRoute,
            isLocalEnvironment,
          })
        : ('hidden' as const),
      save,
    };
  }, [
    isResolved,
    stored,
    isSignedIn,
    regionCode,
    isEmbedded,
    isPublicApplyRoute,
    isLocalEnvironment,
    save,
  ]);

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
}
